<script setup lang="ts">
  import JourneyHandler from '@/components/idf-modal/JourneyHandler.vue'
  import type { Location } from '@/model/location.model.ts'
  import type { JourneyFieldErrors } from '@/types/idf-journey-product'
  import type { EcmrJourneyTemplate } from '@/types/journey-template'
  import type { Organization } from '@/types/organization'
  import { nextTick, onBeforeMount, onUnmounted, ref, type Ref, watch } from 'vue'

  const fromOrganization: Ref<Organization | null> = ref(null)
  const toOrganization: Ref<Organization | null> = ref(null)
  const originLocation: Ref<Location | null> = ref(null)
  const destinationLocation: Ref<Location | null> = ref(null)

  const errors: Ref<JourneyFieldErrors | null> = ref(null)

  watch(fromOrganization, () => {
    originLocation.value = null
  })

  watch(toOrganization, () => {
    destinationLocation.value = null
  })

  watch(
    () => ({
      afzender: fromOrganization.value,
      geadresseerde: toOrganization.value,
      plaatsAflevering: originLocation.value,
      plaatsInontvangstname: destinationLocation.value,
    }),
    state => {
      window.ecmrState = state
    }
  )

  async function setData(data: EcmrJourneyTemplate) {
    //These await ticks are required so that the address fields and location are set correctly
    fromOrganization.value = null
    toOrganization.value = null
    await nextTick()
    fromOrganization.value = data.ecmrAfzender
    toOrganization.value = data.ecmrGeadresseerde
    await nextTick()
    originLocation.value = data.ecmrPlaatsAflevering
    destinationLocation.value = data.ecmrPlaatsInontvangstname
  }

  function validate() {
    const temp: JourneyFieldErrors = {}
    if (fromOrganization.value == null) {
      temp.afzender = 'Afzender is verplicht.'
    }

    if (originLocation.value == null) {
      temp.verzendAdres = 'Herkomst is verplicht.'
    }

    if (toOrganization.value == null) {
      temp.geaddresseerd = 'Geadresseerde is verplicht.'
    }

    if (destinationLocation.value == null) {
      temp.bestemmingAdres = 'Bestemming is verplicht.'
    }

    errors.value = Object.keys(temp).length > 0 ? temp : null
  }

  onBeforeMount(() => {
    window.addEventListener('ecmr-form-validate', validate)
  })

  onUnmounted(() => {
    window.removeEventListener('ecmr-form-validate', validate)
  })

  defineExpose({ setData })
</script>

<template>
  <div class="section-container">
    <JourneyHandler
      v-model:handler-organization="fromOrganization"
      v-model:handler-address="originLocation"
      :error="errors"
      organization-error-key="afzender"
      locationType="origin"
      address-error-key="bestemmingAdres"
      heading-label="Afzender"
      location-label="Herkomst"
    />
    <JourneyHandler
      v-model:handler-organization="toOrganization"
      v-model:handler-address="destinationLocation"
      :error="errors"
      locationType="destination"
      organization-error-key="geaddresseerd"
      address-error-key="bestemmingAdres"
      heading-label="Geadresseerde"
      location-label="Bestemming"
    />
  </div>
</template>

<style scoped>
  .section-container {
    margin: 0 2rem;
  }
</style>
